import { env } from 'next-runtime-env';
import qs from 'qs';

type CardOpeningParams = {
  channelId: number;
} & (
  | { orderId: number; orderItemId?: number }
  | { orderId?: number; orderItemId: number }
);

const getScheme = () => {
  const phase = env('NEXT_PUBLIC_PHASE');
  switch (phase) {
    case 'dev':
      return 'vuddydev';
    case 'sandbox':
      return 'vuddysandbox';
    default:
      return 'vuddy';
  }
};
const SCHEME = getScheme();

const getUrl = (url: string, params?: Record<string, unknown>) => {
  const baseUrl = `${SCHEME}://${url}`;
  if (params && Object.keys(params).length > 0) {
    return `${baseUrl}?${qs.stringify(params)}`;
  }
  return baseUrl;
};

export const deepLinks = {
  home: getUrl('home'),
  login: getUrl('login'),
  classList: () => getUrl('class'),
  classDetail: (params: { id: number | string }) =>
    getUrl('class', { id: params.id }),
  mediaGoodsViewer: (params: { mediaItemId: number }) =>
    getUrl('media_goods_viewer', {
      user_media_item_id: params.mediaItemId,
    }),
  cardOpening: (params: CardOpeningParams) =>
    getUrl('open_results', {
      ...(params.orderId && { order_id: params.orderId }),
      ...(params.orderItemId && { order_item_id: params.orderItemId }),
      channel_id: params.channelId,
    }),
  channelDetail: (params: {
    channelId: number;
    tab: 'Home' | 'Shop' | 'MyBox' | 'Collection';
    collectionId?: number;
  }) =>
    getUrl('channel', {
      channel_id: params.channelId,
      tab: params.tab,
      media_item_collection_id: params.collectionId,
    }),
  openUrl: (url: string, withNewWebView = true) => {
    return getUrl('web', {
      // external이 true면 외부 브라우저, false면 인앱 웹뷰
      external: !withNewWebView,
      url,
      // 동일 스킴 호출 중복 무시 방지를 위한 nonce
      _ts: Date.now(),
    });
  },
  gachaDraw: (params: {
    userGachaTicketIds?: string;
    gachaRequestId?: number;
    fromSalePage: boolean;
  }) => {
    return getUrl('gacha/draw', {
      user_gacha_ticket_ids: params.userGachaTicketIds,
      gacha_request_id: params.gachaRequestId,
      from_sale_page: params.fromSalePage,
    });
  },
};

const isVuddyScheme = (protocol: string) =>
  protocol.replace(':', '').startsWith('vuddy');

/**
 * iOS 앱 웹뷰에서 동일 딥링크(`vuddy*://web?...`)를 연속 호출하면
 * 앱에서 중복으로 판단해 무시하는 케이스가 있어 `_ts`를 매 클릭마다 갱신합니다.
 */
export const ensureUniqueVuddyWebDeepLink = (rawHref: string): string => {
  try {
    const url = new URL(rawHref);
    if (!isVuddyScheme(url.protocol)) return rawHref;
    if (url.hostname !== 'web') return rawHref;

    url.searchParams.set('_ts', String(Date.now()));
    return url.toString();
  } catch {
    return rawHref;
  }
};
